/*
 * Yomiri Plugin Validator & Security Sanitizer
 * Ensures all installed plugins adhere to YPP-1 standards without malicious vectors.
 */

import type { YomiriPluginManifest } from './PluginTypes.ts';

export class PluginValidator {
  private static readonly SAFE_URL_PATTERN = /^https?:\/\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=]+$/i;
  private static readonly ID_PATTERN = /^[a-z0-9]+([._-][a-z0-9]+)*$/i;

  public static validateManifest(input: unknown): { valid: boolean; manifest?: YomiriPluginManifest; error?: string } {
    if (!input || typeof input !== 'object' || Array.isArray(input)) {
      return { valid: false, error: 'El manifiesto debe ser un objeto JSON válido' };
    }

    const raw = input as Record<string, any>;

    // 1. Validate ID
    if (typeof raw.id !== 'string' || !raw.id.trim() || !this.ID_PATTERN.test(raw.id.trim())) {
      return { valid: false, error: 'ID de plugin inválido. Debe contener únicamente caracteres alfanuméricos, puntos o guiones (ej. "tv.argentina.live")' };
    }

    // 2. Validate Name & Version
    if (typeof raw.name !== 'string' || !raw.name.trim()) {
      return { valid: false, error: 'El plugin debe tener un nombre visible ("name")' };
    }

    if (typeof raw.version !== 'string' || !raw.version.trim()) {
      return { valid: false, error: 'El plugin debe especificar una versión semver ("version")' };
    }

    // 3. Validate Types
    if (!Array.isArray(raw.types) || raw.types.length === 0) {
      return { valid: false, error: 'El plugin debe declarar al menos un tipo de contenido ("types": ["tv", "movie", etc.])' };
    }

    // 4. Validate Resources
    const validResources = new Set(['catalog', 'meta', 'stream', 'reader', 'subtitles']);
    if (!Array.isArray(raw.resources) || raw.resources.length === 0) {
      return { valid: false, error: 'El plugin debe implementar al menos un recurso ("resources": ["catalog", "stream", etc.])' };
    }

    const resources = raw.resources.map((r: any) => typeof r === 'string' ? r : r?.name).filter(r => validResources.has(typeof r === 'string' ? r : r?.name));
    if (resources.length === 0) {
      return { valid: false, error: 'Ningún recurso declarado es válido. Recursos soportados: catalog, meta, stream, reader, subtitles' };
    }

    // 5. Validate Catalogs
    const catalogs = Array.isArray(raw.catalogs)
      ? raw.catalogs
          .filter((c: any) => c && typeof c === 'object' && typeof c.id === 'string' && typeof c.type === 'string')
          .map((c: any) => ({
            type: String(c.type || 'other'),
            id: String(c.id).trim(),
            name: String(c.name || c.id).trim(),
            extra: Array.isArray(c.extra)
              ? c.extra.filter((e: any) => e && typeof e.name === 'string').map((e: any) => ({
                  name: String(e.name),
                  isRequired: Boolean(e.isRequired),
                  options: Array.isArray(e.options) ? e.options.map(String) : undefined,
                }))
              : undefined,
          }))
      : [];

    // 6. Sanitize Icon & Repository URLs
    let icon: string | undefined = undefined;
    if (!raw.icon && typeof raw.logo === 'string') raw.icon = raw.logo;
    if (typeof raw.icon === 'string' && (this.SAFE_URL_PATTERN.test(raw.icon) || raw.icon.startsWith('data:image/'))) {
      icon = raw.icon;
    }

    let repository: string | undefined = undefined;
    if (typeof raw.repository === 'string' && this.SAFE_URL_PATTERN.test(raw.repository)) {
      repository = raw.repository;
    }

    const manifest: YomiriPluginManifest = {
      id: raw.id.trim(),
      name: raw.name.trim().slice(0, 80),
      version: raw.version.trim().slice(0, 24),
      description: typeof raw.description === 'string' ? raw.description.trim().slice(0, 500) : '',
      icon,
      author: typeof raw.author === 'string' ? raw.author.trim().slice(0, 80) : undefined,
      repository,
      types: raw.types.map(String),
      resources: resources as YomiriPluginManifest['resources'],
      catalogs,
      resourceRules: raw.resources.map((r:any)=>typeof r==='string'?{name:r}:{name:r.name,types:r.types,idPrefixes:r.idPrefixes}),
      behaviorHints: typeof raw.behaviorHints === 'object' && raw.behaviorHints ? {
        defaultSection: typeof raw.behaviorHints.defaultSection === 'string' ? raw.behaviorHints.defaultSection.trim().toLowerCase() : undefined,
        sectionTitle: typeof raw.behaviorHints.sectionTitle === 'string' ? raw.behaviorHints.sectionTitle.trim().slice(0, 40) : undefined,
        sectionIcon: typeof raw.behaviorHints.sectionIcon === 'string' ? raw.behaviorHints.sectionIcon.trim() : undefined,
        configurable: Boolean(raw.behaviorHints.configurable),
        staticData: Boolean(raw.behaviorHints.staticData),
      } : undefined,
    };

    return { valid: true, manifest };
  }

  public static isSafeUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
      return false;
    }
  }
}
